import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { NewExerciseModal } from '../../../../pages/exercises/new-exercise/NewExerciseModal';
import { IExercise } from '../../../../types/interfaces/exercise';
import { IProgramSet } from '../../../../types/interfaces/program-set';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import AddActionButton from '../../../designe/ActionButtons/AddActionButton';
import DeleteActionButton from '../../../designe/ActionButtons/DeleteActionButton';
import SaveActionButton from '../../../designe/ActionButtons/SaveActionButton';
import UploadActionButton from '../../../designe/ActionButtons/UploadActionButton';
import AppSelectExercise from '../../../designe/textfields/app-select-exercise/AppSelectExercise';
import AppDialog from '../../app-dialog/AppDialog';
import SavedProgramSet from '../../saved-program-set-modal/SavedProgramSet';
import SetItemsListEdit from '../SetItemsListEdit/SetItemsListEdit';
import { IUseSetGroupEditTable } from '../interfaces';
import { useStyles } from './SetEdit.styles';
import SaveSet from './save-set/SaveSet';
import { useSetEditComponent } from './useSetEditComponent';

interface ISetEditProps {
  set: IProgramSet | ITrainingSet;
  setsCount: number;
  isSuperset: boolean;
  useSetGroupEditTable: IUseSetGroupEditTable<IProgramSet | ITrainingSet>;
}

function SetEdit(props: ISetEditProps) {
  const { set, useSetGroupEditTable, setsCount } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { onRemoveSetsBySetNumber, options, submitCalled } =
    useSetGroupEditTable;
  const {
    exercises,
    onChangeExercise,
    onAddSetItemClick,
    showSavedProgramItemModal,
    setShowSavedProgramItemModal,
    onReplaceSavedProgramSet,
    showNewExerciseModal,
    setShowNewExerciseModal,
    showSaveSetModal,
    setShowSaveSetModal,
    onSaveSet,
  } = useSetEditComponent(set, useSetGroupEditTable);

  const showRemoveSetButton =
    (options.removeSetMode === 'set' || options.removeSetMode === 'both') &&
    setsCount > 1 &&
    !!onRemoveSetsBySetNumber;
  const hideSaveSet = options.hideSaveSet;

  return (
    <div className={classes.setWrapper}>
      <div className={classes.setHeader}>
        <AppSelectExercise
          dataOnboarding="newTraining.exerciseSelector"
          exercises={exercises}
          value={set.exercise}
          onAddButtonClick={() => setShowNewExerciseModal(true)}
          onChange={onChangeExercise}
          error={submitCalled && !set.exercise}
          helperText={t('formikValidation.exerciseRequired')}
        />
        {(showRemoveSetButton ||
          !options.hideSavedSetUploading ||
          (!hideSaveSet && !!set.exerciseId)) && (
          <Box
            display="flex"
            gap={1}
            paddingBottom={1.5}
            justifyContent="space-between">
            {showRemoveSetButton && (
              <DeleteActionButton
                onClick={() => {
                  onRemoveSetsBySetNumber(set.setNumber, set.orderNumber);
                }}
              />
            )}
            {!options.hideSavedSetUploading && (
              <UploadActionButton
                onClick={() => setShowSavedProgramItemModal(true)}
              />
            )}
            {!hideSaveSet && set.exerciseId && (
              <SaveActionButton onClick={() => setShowSaveSetModal(true)} />
            )}
          </Box>
        )}
      </div>
      {set.exerciseId && (
        <div className={classes.setItemsListWrapper}>
          <SetItemsListEdit
            set={set}
            useSetGroupEditTable={useSetGroupEditTable}
          />
          <Box className={classes.setItemsListFooter}>
            <AddActionButton
              data-onboarding="newTraining.addSetItem"
              onClick={() => onAddSetItemClick()}></AddActionButton>
          </Box>
        </div>
      )}
      <AppDialog
        open={!!showSavedProgramItemModal}
        onClose={() => setShowSavedProgramItemModal(false)}
        title={t('setTable.uploadSavedProgramSet')}>
        <SavedProgramSet
          onCancel={() => setShowSavedProgramItemModal(false)}
          onSubmitSavedProgramSet={(savedSet) => {
            setShowSavedProgramItemModal(false);
            onReplaceSavedProgramSet({
              ...savedSet,
              orderNumber: set.orderNumber,
            });
          }}
        />
      </AppDialog>
      <AppDialog
        open={!!showSaveSetModal}
        onClose={() => setShowSaveSetModal(false)}
        title={t('setTable.saveSet')}>
        <SaveSet
          onCancel={() => setShowSaveSetModal(false)}
          onSaveSet={onSaveSet}
        />
      </AppDialog>
      <NewExerciseModal
        showNewExerciseModal={showNewExerciseModal}
        setShowNewExerciseModal={setShowNewExerciseModal}
        onExerciseCreate={(exercise: IExercise) => onChangeExercise(exercise)}
      />
    </div>
  );
}

export default SetEdit;
